import type { ElementType, MouseEventHandler, ReactNode } from 'react';
import { Box, Button, ButtonProps, Typography, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { COLORS } from '../../theme/colors';

const mergeSx = (base: SystemStyleObject<Theme>, sx: SxProps<Theme> = []): SxProps<Theme> => {
  const sxArray = Array.isArray(sx) ? sx : [sx];
  return [base, ...sxArray] as SxProps<Theme>;
};

type SectionTagProps = {
  label: ReactNode;
  icon?: ReactNode;
};

// ── SectionTag ───────────────────────────────────────────────
// Small label pill above section titles
export function SectionTag({ label, icon }: SectionTagProps): JSX.Element {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        borderRadius: '50px',
        border: `1px solid ${COLORS.purple[500]}44`,
        background: `${COLORS.purple[500]}18`,
        mb: 2,
      }}
    >
      {icon && <Box sx={{ fontSize: '0.85rem' }}>{icon}</Box>}
      <Typography
        variant="caption"
        sx={{
          color: COLORS.purple[400],
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

type GradientTextProps = {
  children: ReactNode;
  component?: ElementType;
  sx?: SxProps<Theme>;
};

// ── GradientText ─────────────────────────────────────────────
// Text with brand gradient
export function GradientText({ children, component = 'span', sx = {} }: GradientTextProps): JSX.Element {
  return (
    <Box
      component={component}
      sx={mergeSx(
        {
          background: COLORS.gradients.brand,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          display: 'inline',
        },
        sx,
      )}
    >
      {children}
    </Box>
  );
}

type SectionHeaderProps = {
  tag?: ReactNode;
  tagIcon?: ReactNode;
  title: ReactNode;
  highlight?: ReactNode;
  subtitle?: ReactNode;
  align?: 'left' | 'center' | 'right';
  dark?: boolean;
};

// ── SectionHeader ─────────────────────────────────────────────
// Centered section title + subtitle block
export function SectionHeader({
  tag,
  tagIcon,
  title,
  highlight,
  subtitle,
  align = 'center',
  dark = false,
}: SectionHeaderProps): JSX.Element {
  return (
    <Box sx={{ textAlign: align, mb: { xs: 5, md: 7 } }}>
      {tag && (
        <Box sx={{ display: 'flex', justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
          <SectionTag label={tag} icon={tagIcon} />
        </Box>
      )}
      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: '2rem', md: '2.75rem' },
          color: dark ? '#fff' : 'text.primary',
          mb: 1.5,
        }}
      >
        {title}{' '}
        {highlight && <GradientText>{highlight}</GradientText>}
      </Typography>
      {subtitle && (
        <Typography
          variant="body1"
          sx={{
            color: dark ? COLORS.gray[400] : 'text.secondary',
            maxWidth: 560,
            mx: align === 'center' ? 'auto' : 0,
            fontSize: '1rem',
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

type GlowCardProps = {
  children: ReactNode;
  sx?: SxProps<Theme>;
  active?: boolean;
};

// ── GlowCard ─────────────────────────────────────────────────
// Dark glassy card with hover glow
export function GlowCard({ children, sx = {}, active = false }: GlowCardProps): JSX.Element {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={mergeSx(
        {
          borderRadius: '16px',
          border: `1px solid ${active ? COLORS.purple[500] : isDark ? COLORS.dark[400] : COLORS.light[400]}`,
          background: isDark
            ? active
              ? 'rgba(147,51,234,0.12)'
              : COLORS.gradients.cardDark
            : active
              ? 'rgba(147,51,234,0.06)'
              : '#ffffff',
          p: 3,
          transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-4px)',
            borderColor: COLORS.purple[500],
            boxShadow: '0 8px 32px rgba(147,51,234,0.2)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: active ? COLORS.gradients.brand : 'transparent',
            transition: 'background 0.3s',
          },
        },
        sx,
      )}
    >
      {children}
    </Box>
  );
}

type GradientButtonProps = ButtonProps;

// ── GradientButton ────────────────────────────────────────────
export function GradientButton({ children, sx, ...props }: GradientButtonProps): JSX.Element {
  return (
    <Button
      {...props}
      sx={[
        {
          borderRadius: '999px',
          px: 3,
          py: 1.25,
          fontWeight: 700,
          textTransform: 'none',
          color: '#ffffff',
          background: COLORS.gradients.brand,
          boxShadow: '0 0 28px rgba(168,85,247,0.35)',
          transition: 'all 0.25s ease',
          '&:hover': {
            background: COLORS.gradients.brand,
            transform: 'translateY(-2px)',
            boxShadow: '0 0 34px rgba(168,85,247,0.48)',
          },
          '&.Mui-disabled': {
            color: 'rgba(255,255,255,0.7)',
            background: COLORS.gradients.brand,
            opacity: 0.65,
            boxShadow: 'none',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Button>
  );
}

type IconBadgeProps = {
  children: ReactNode;
  size?: number;
};

// ── IconBadge ─────────────────────────────────────────────────
export function IconBadge({ children, size = 48 }: IconBadgeProps): JSX.Element {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '12px',
        background: `${COLORS.purple[500]}22`,
        border: `1px solid ${COLORS.purple[500]}44`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: size * 0.4,
      }}
    >
      {children}
    </Box>
  );
}

type StepNumberProps = {
  number: ReactNode;
};

// ── StepNumber ────────────────────────────────────────────────
export function StepNumber({ number }: StepNumberProps): JSX.Element {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '10px',
        background: COLORS.dark[600],
        border: `1px solid ${COLORS.dark[400]}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2,
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Syne", sans-serif',
          fontWeight: 700,
          fontSize: '0.8rem',
          color: COLORS.gray[400],
        }}
      >
        {number}
      </Typography>
    </Box>
  );
}
